import React from "react";
import { FileLinks } from "components/Links";
import { formatAssessmentAnswer } from "utils";

const ASSESSMENT_CONFIGS = {
  missionRole: {
    assessmentableType: "MissionRole",
    category: "mission_role_assessment",
    assessmentKey: "assessment",
    assessmentType: "MissionRoleAssessment",
  },
};

const FILE_UPLOAD_TYPES = {
  pdf: { label: "PDF", extensions: [".pdf"] },
  image: { label: "Image", extensions: [".jpg", ".jpeg", ".png", ".gif", ".webp"] },
  document: { label: "Document", extensions: [".doc", ".docx", ".txt"] },
  spreadsheet: { label: "Spreadsheet", extensions: [".xls", ".xlsx", ".csv"] },
};

const FILE_UPLOAD_LIMITS = {
  maxFileSize: 10,
  maxFiles: 10,
};

const FILE_UPLOAD_DEFAULTS = {
  acceptedTypes: ["pdf"],
  maxFileSize: "5",
  maxFiles: "1",
};

const BYTES_PER_MEGABYTE = 1024 * 1024;

const QUESTION_SETTINGS = {
  charactersLimit: "charactersLimit",
  options: "options",
  acceptedTypes: "acceptedTypes",
  maxFileSize: "maxFileSize",
  maxFiles: "maxFiles",
};

const QUESTION_TYPE_CONFIGS = {
  textarea: {
    label: "Text Area",
    settings: [QUESTION_SETTINGS.charactersLimit],
    summary: ({ properties }) => ({
      id: "Missions.Roles.QuestionsList.CharactersLimit",
      values: { limit: properties?.charactersLimit },
    }),
  },
  select: {
    label: "Multiple Choice",
    settings: [QUESTION_SETTINGS.options],
    summary: ({ options }) => optionsSummary(options),
  },
  checkBoxGroup: {
    label: "Multiple Select",
    settings: [QUESTION_SETTINGS.options],
    summary: ({ options }) => optionsSummary(options),
  },
  fileDropzone: {
    label: "File Upload",
    settings: [QUESTION_SETTINGS.acceptedTypes, QUESTION_SETTINGS.maxFileSize, QUESTION_SETTINGS.maxFiles],
    summary: ({ properties }) => ({
      id: "Missions.Roles.QuestionsList.UploadRules",
      values: {
        maxFiles: Number(properties?.maxFiles),
        maxFileSize: properties?.maxFileSize,
        types: fileTypeLabels(properties?.acceptedTypes),
      },
    }),
  },
};

const QUESTION_TYPE_OPTIONS = Object.entries(QUESTION_TYPE_CONFIGS).map(([value, { label }]) => ({ label, value }));

const FILE_TYPE_OPTIONS = Object.entries(FILE_UPLOAD_TYPES).map(([value, { label }]) => ({ label, value }));

const fileTypeOptions = (acceptedTypes) =>
  (acceptedTypes || []).map((value) => FILE_TYPE_OPTIONS.find((option) => option.value === value)).filter(Boolean);

const fileTypeLabels = (acceptedTypes) =>
  (acceptedTypes || []).map((type) => FILE_UPLOAD_TYPES[type]?.label || type).join(", ");

const fileDropzoneProps = ({ acceptedTypes, maxFileSize, maxFiles } = {}) => ({
  accept: (acceptedTypes || []).flatMap((type) => FILE_UPLOAD_TYPES[type]?.extensions || []).join(","),
  hintFileTypeLabel: fileTypeLabels(acceptedTypes),
  maxSize: Number(maxFileSize) * BYTES_PER_MEGABYTE,
  maxFiles: Number(maxFiles),
});

const optionsSummary = (options) =>
  options?.length
    ? {
        id: "Missions.Roles.QuestionsList.Options",
        values: { options: options.map(({ label }) => label).join(", ") },
      }
    : null;

const renderAssessmentAnswer = (answer, { type, options, properties } = {}) => {
  const text = formatAssessmentAnswer(answer, { options, ...properties });

  return text && type === "fileDropzone" ? <FileLinks files={answer} /> : text;
};

export default ASSESSMENT_CONFIGS;

export {
  QUESTION_SETTINGS,
  QUESTION_TYPE_CONFIGS,
  QUESTION_TYPE_OPTIONS,
  FILE_UPLOAD_TYPES,
  FILE_UPLOAD_LIMITS,
  FILE_UPLOAD_DEFAULTS,
  FILE_TYPE_OPTIONS,
  fileTypeOptions,
  fileDropzoneProps,
  renderAssessmentAnswer,
};
